import { useState } from 'react';
import { useNotifications } from '@/hooks/useNotifications';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ShieldAlert, RefreshCw, AlertCircle, CheckCircle, Filter } from 'lucide-react';

const typeIcons = {
  needs_intervention: AlertTriangle,
  dispute_created: ShieldAlert,
  communication_failure: AlertCircle,
  refund: RefreshCw,
  fraud_block: ShieldAlert,
};

const severityBadge = {
  critical: 'bg-red-500/10 text-red-400 border-red-500/20',
  warning: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  info: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
};

export default function NotificationsPage() {
  const { notifications, markAsRead } = useNotifications();
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const filtered = notifications.filter(n => {
    if (severityFilter !== 'all' && n.severity !== severityFilter) return false;
    if (typeFilter !== 'all' && n.type !== typeFilter) return false;
    return true;
  });

  const types = [...new Set(notifications.map(n => n.type))];

  return (
      <div className="space-y-6" data-testid="notifications-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-2xl font-semibold">Notifications</h1>
            <p className="text-muted-foreground text-sm">Real-time alerts for critical events</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center" data-testid="notification-filters">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select 
            className="bg-muted border border-border text-sm rounded-lg px-3 py-1.5"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            data-testid="severity-filter"
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          <select 
            className="bg-muted border border-border text-sm rounded-lg px-3 py-1.5"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="type-filter"
          >
            <option value="all">All Types</option>
            {types.map(t => (
              <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground ml-2">{filtered.length} notifications</span>
        </div>

        {/* Notification List */}
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            filtered.map((n) => {
              const Icon = typeIcons[n.type] || AlertCircle;
              return (
                <div 
                  key={n.id} 
                  className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${n.read_at ? 'border-white/5 bg-card/30 opacity-70' : 'border-border bg-card'}`}
                  data-testid={`notif-row-${n.id}`}
                >
                  <div className={`p-2 rounded-lg ${n.severity === 'critical' ? 'bg-red-500/10' : n.severity === 'warning' ? 'bg-yellow-500/10' : 'bg-blue-500/10'}`}>
                    <Icon className={`h-4 w-4 ${n.severity === 'critical' ? 'text-red-400' : n.severity === 'warning' ? 'text-yellow-400' : 'text-blue-400'}`} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[10px] ${severityBadge[n.severity] || ''}`}>
                        {n.severity}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground font-mono">{n.type.replace(/_/g, ' ')}</span>
                    </div>
                    <p className="text-sm font-medium">{n.message}</p>
                    {n.load_id && (
                      <p className="text-xs text-muted-foreground mt-1">Load: {n.load_id.slice(0, 8)}...</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  {!n.read_at && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="shrink-0"
                      onClick={() => markAsRead(n.id)}
                      data-testid={`mark-read-${n.id}`}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
  );
}

